<template>
  <div class="editor-container">
    <div class="editor-header">
      <div class="widget-type">
        <router-link
          :to="{ name: 'pdf_report_editor', params: { report_id: reportId } }">
          <b-button
            type="is-light"
            class="back-btn mr-5"
            icon-right="arrow-left" />
        </router-link>
        <div class="report-breadcrumbs">
          <div>
            <h4>Report</h4>
            <h2>{{ reportData?.name || "Pdf Report" }}</h2>
          </div>

          <div class="arrow-right">
            <i class="material-icons">chevron_right </i>
          </div>

          <div>
            <h4>Module</h4>
            <h2>{{ module?.title || moduleConfigs.name }}</h2>
          </div>
        </div>
      </div>

      <div class="controls">
        <a-button
          type="button ml-2"
          :disabled="!isDirty"
          :loading="isSaving"
          hotkeys="ctrl+shift+s"
          class="button is-primary"
          @click="save">
          Publish Changes
        </a-button>
        <a-button
          type="button ml-2"
          :disabled="!isDirty"
          hotkeys="ctrl+shift+d"
          class="button is-light"
          @click="getData">
          Discard Changes
        </a-button>
      </div>
    </div>
    <div class="editor-body">
      <div class="editor-and-preview">
        <splitpanes class="default-theme">
          <pane
            v-if="module"
            min-size="20"
            class="pane">
            <div class="block editor p-5">
              <b-field
                label="Title"
                class="input-field"
                message="">
                <ScopeLabelInput
                  v-model="module.title"
                  placeholder="Title" />
              </b-field>

              <b-field class="input-field">
                <b-switch
                  v-model="module.show_logo"
                  type="is-info">
                  Show Logo
                </b-switch>
              </b-field>

              <b-field class="input-field">
                <b-switch
                  v-model="module.show_time_range"
                  type="is-info">
                  Show Time Range
                </b-switch>
              </b-field>
            </div>
          </pane>
          <pane min-size="20">
            <PreviewAndJSONEditor
              v-model="module"
              type="module"
              :default-time-range="reportData?.default_time_range" />
          </pane>
        </splitpanes>
      </div>
    </div>
    <b-loading
      v-model="isLoading"
      :is-full-page="true"
      :can-cancel="false" />

    <b-modal
      v-model="showUnsavedModal"
      :destroy-on-hide="true"
      trap-focus
      close-button-aria-label="Close"
      aria-modal
      custom-class="unsaved-modal-simple">
      <div class="unsaved-modal-content">
        <div class="unsaved-modal-title-row">
          <span class="unsaved-modal-title">You have unsaved changes</span>
        </div>
        <div class="unsaved-modal-body">
          <p>Do you want to save your changes before leaving this page?</p>
        </div>
        <div class="unsaved-modal-actions custom-unsaved-modal-actions">
          <b-button
            type="is-outlined"
            size="is-small"
            class="cancel-btn"
            @click="closeUnsavedModal">
            Cancel
          </b-button>
          <div class="right-actions">
            <b-button
              type="is-muted"
              size="is-small"
              class="discard-btn"
              @click="discardAndNavigate">
              Discard
            </b-button>
            <b-button
              size="is-small"
              class="save-btn"
              @click="saveAndNavigate">
              Save Changes
            </b-button>
          </div>
        </div>
      </div>
    </b-modal>
  </div>
</template>

<script>
  import uniqid from 'uniqid'
  import { Splitpanes, Pane } from 'splitpanes'
  import PreviewAndJSONEditor from '@/components/pdf_report/PreviewAndJsonEditor.vue'
  import ScopeLabelInput from '@/components/pdf_report/ScopeLabelInput.vue'

  export default {
    name: 'TitleModuleEditor',
    components: {
      Splitpanes,
      Pane,
      PreviewAndJSONEditor,
      ScopeLabelInput,
    },
    beforeRouteLeave (to, from, next) {
      if (this.isDirty) {
        this.showUnsavedModal = true
        this.nextRoute = { to, from, }
        this.nextRouteCallback = next
      } else {
        next()
      }
    },
    props: {},
    data () {
      return {
        moduleId: null,
        reportId: null,
        module: null,
        reportData: null,

        loadingModulePreview: false,

        moduleConfigs: {
          name: 'Title',
          type: 'SectionDividerModule',
          icon: require('@/assets/icons/section-divider.svg'),
        },

        showCode: false,
        modulePreviewURL: '',

        selectedRowKey: null,
        selectedRowModule: null,

        isLoading: false,
        isDirty: false,
        isSaving: false,

        showUnsavedModal: false,
        nextRoute: null,
        nextRouteCallback: null,
      }
    },

    computed: {},
    watch: {
      module: {
        handler () {
          this.isDirty = true
        },
        deep: true,
      },
    },

    async mounted () {
      this.moduleId = this.$route.params.module_id
      this.reportId = this.$route.params.report_id

      await this.getData()
      this.isLoading = false
      if (!this.module.key) {
        this.module.key = uniqid()
      }

      await this.$store.dispatch('app/reportsConfig/refreshOptions')
    },
    methods: {
      async getData () {
        this.isLoading = true
        const response = await this.$apis.pdfReports.getPdfModuleData(
          this.moduleId
        )
        this.module = response
        this.reportData = await this.$apis.pdfReports.getPdfReportData(
          this.reportId
        )
        await this.$nextTick()
        this.isDirty = false
        this.isLoading = false
      },

      removeReport (key) {
        this.$emit('remove', key)
      },

      onError (error) {
        console.log('error', error)
      },

      showModulePicker () {},

      makeInputsDirty () {},

      async save () {
        this.isSaving = true
        this.isDirty = false
        const response = await this.$apis.pdfReports.updatePdfReportModule(
          this.module
        )
        this.isSaving = false

        if (!response) {
          this.isDirty = true
          this.$buefy.toast.open({
            message: 'Backend Error, Report this to the devs',
            type: 'is-warning',
          })
          return
        }

        if (response?.errors) {
          this.isDirty = true
          this.$buefy.toast.open({
            message: 'Error saving module, Fix the errors and try again',
            type: 'is-warning',
          })
        } else {
          this.module = response
          this.$buefy.toast.open({
            message: 'Changes Saved',
            type: 'is-success',
            position: 'is-bottom',
          })
          await this.$nextTick()
          this.isDirty = false
        }
      },

      async deleteModule () {
        this.isLoading = true

        if (await this.$apis.pdfReports.deletePdfReportModule(this.moduleId)) {
          this.$router.push({
            name: 'pdf_report_editor',
            params: { report_id: this.reportId, },
          })
        } else {
          this.$buefy.toast.open({
            message: 'Error deleting module',
            type: 'is-danger',
            position: 'is-bottom',
          })
        }
        this.isLoading = true
      },

      openTrackerRowModule (index = null) {
        if (index === null) {
          this.selectedRowModule = null
          this.selectedRowKey = null
          return
        }

        this.selectedRowModule = this.module.tracker_rows[index]
        this.selectedRowKey = this.selectedRowModule.key
      },

      switchOutputMode () {
        this.showCode = !this.showCode
      },

      closeUnsavedModal () {
        this.showUnsavedModal = false
        this.nextRoute = null
        this.nextRouteCallback = null
      },

      async discardAndNavigate () {
        await this.getData()
        this.showUnsavedModal = false
        if (this.nextRouteCallback) {
          this.nextRouteCallback()
        }
        this.nextRoute = null
        this.nextRouteCallback = null
      },

      async saveAndNavigate () {
        await this.save()
        this.showUnsavedModal = false
        if (this.nextRouteCallback) {
          this.nextRouteCallback()
        }
        this.nextRoute = null
        this.nextRouteCallback = null
      },
    },
  }
</script>

<style lang="scss" scoped>
.report-breadcrumbs {
  display: flex;

  h2 {
    font-weight: 400;
    font-size: 20px;
    margin: 0;
  }

  h4 {
    font-weight: 400;
    font-size: 12px;
    margin: 0;
    color: $grayscale-3;
  }

  .arrow-right {
    display: flex;
    align-items: center;
    margin: 0 10px;
    font-size: 20px;
    color: #b0b0b0;
  }
}
.back-btn {
  font-size: 20px !important;
  border-radius: 100% !important;
}
.editor-container {
  padding: 20px;
  width: 100%;
  height: 100%;
  margin: auto !important;
  background-color: white;
  display: flex;
  flex-direction: column;
}

.editor-body {
  display: flex;
  height: 100%;
  overflow: hidden;
}

.block {
  border: $border-1;
  border-radius: 3px;
  height: 100%;
  flex-direction: column;
  display: flex;

  .block-header {
    min-height: 40px;
    align-items: center;
    padding: 0 5px;
    border-bottom: $border-1;
    background-color: white;
    display: flex;

    h4 {
      font-size: 14px;
      font-style: normal;
      font-weight: 400;
      padding: 0 10px;
      text-transform: capitalize;
      margin: 0;
      color: black;
    }
  }
  .block-body {
    padding: 10px 10px;
    background-color: #fbfbfb;
    height: 100%;
    width: 100%;
    flex-direction: column;
    display: flex;
  }

  &.layout {
    max-height: 100%;
    overflow: hidden;
    margin-right: 10px;
    width: 300px;
  }

  &.editor {
    width: 100%;
  }

  &.preview {
    height: 100%;
    .block-header {
      width: 100%;
    }

    .block-body {
      width: 100%;
      height: 100%;
      display: flex;
      overflow: hidden;
    }

    .output {
      background-color: white;
      height: 100%;
      overflow: auto;
    }
  }
}

.editor-and-preview {
  width: 100%;
}

.editor-header {
  margin-bottom: 20px;

  display: flex;
  padding-top: 10px;
  .widget-type {
    display: flex;
    font-weight: 400;
    font-size: 15px;
    align-items: center;
    img {
      width: 35px;
      height: 35px;
      margin-right: 10px;
      border: $border-1;
      border-radius: 8px;
      padding: 5px;
      background-color: rgba(0, 0, 0, 0.05);
    }
  }

  .controls {
    margin-left: auto;
    display: flex;
    align-items: center;

    .button {
      padding: 0 15px;
      font-size: 12px;
    }
  }
}

.mini-view-item {
  cursor: pointer;
  transition: background-color 0.3s ease-in-out, color 0.3s;
  width: 100%;
  padding: 10px;
  display: flex;
  border-radius: 4px;
  align-items: center;
  font-size: 15px;
  background-color: white;
  border: 1px solid #f5f5f5;
  box-shadow: 0px 1px 4px 0px rgba(40, 39, 43, 0.02),
    0px 1px 2px 0px rgba(40, 39, 43, 0.06);

  img {
    width: 15px;
    height: 15px;
    margin-right: 10px;
  }
  &:hover {
    background-color: rgba(0, 0, 0, 0.03);
    transition: background-color 0.1s;
    border: $border-1;
  }
  &.selected {
    background-color: $primary;
    color: white;
    img {
      filter: brightness(0) invert(1);
    }
  }
}

.unsaved-modal-simple {
  .modal-content {
    background: transparent !important;
    box-shadow: none !important;
    padding: 0 !important;
  }
}
.unsaved-modal-content {
  background: #fff;
  border-radius: 8px;
  padding: 24px 24px 16px 24px;
  min-width: 320px;
  max-width: 400px;
  margin: 0 auto;
  box-shadow: 0 4px 24px rgba(0,0,0,0.08);
  display: flex;
  flex-direction: column;
  align-items: stretch;
}
.unsaved-modal-title-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
}
.unsaved-modal-title {
  font-size: 18px;
  font-weight: 600;
  color: #222;
}
.unsaved-modal-close {
  background: none;
  border: none;
  font-size: 22px;
  color: #888;
  cursor: pointer;
  line-height: 1;
  padding: 0 4px;
}
.unsaved-modal-body {
  font-size: 15px;
  color: #444;
  margin-bottom: 18px;
}
.unsaved-modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
}

.custom-unsaved-modal-actions {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 0;
  padding-top: 4px;
}
.custom-unsaved-modal-actions .cancel-btn {
  min-width: 90px;
  border-radius: 4px !important;
  color: black !important;
  font-size: 14px !important;
  border: 1px solid #e0e0e0 !important;
}
.custom-unsaved-modal-actions .right-actions {
  display: flex;
  gap: 8px;
}
.custom-unsaved-modal-actions .discard-btn {
  background: #f5f5f5 !important;
  color: black !important;
  border: 1px solid #e0e0e0 !important;
  border-radius: 4px !important;
  font-size: 14px !important;
}
.custom-unsaved-modal-actions .save-btn {
  background: #111 !important;
  color: #fff !important;
  border: 1px solid #111 !important;
  border-radius: 4px !important;
  font-size: 14px !important;
}
</style>
